<template>
    <div>
        <!-- Toolbar Slot -->
        <div v-if="$slots.toolbar" class="ppv2-mb-5">
            <slot name="toolbar"></slot>
        </div>

        <!-- Table with Horizontal Scroll -->
        <div class="ppv2-overflow-x-auto">
            <table ref="tableElement" class="ppv2-w-full ppv2-min-w-max ppv2-overflow-x-auto">
                <!-- Table Header -->
                <thead>
                    <tr class="headerColor ppv2-border-t ppv2-border-b ppv2-border-white ppv2-border-opacity-5">
                        <!-- Checkbox Column -->
                        <th v-if="enableMultiSelect" class="ppv2-px-4 ppv2-py-5 ppv2-text-center">
                            <BaseCheckbox
                                size="md"
                                :value="isAllSelected"
                                @input="handleSelectAllToggle"
                                :has-border-checked="true"
                                classAdd="md:ppv2-pl-[15px]"
                            />
                        </th>

                        <!-- Dynamic Columns -->
                        <th
                            v-for="(column, index) in columns"
                            :key="column.key"
                            class="ppv2-px-4 py-1"
                            :class="[
                                column.headerClass || '',
                                column.sortable !== false ? 'ppv2-cursor-pointer text-nowrap' : '',
                                getAlignmentClass(column, index),
                            ]"
                            @click="column.sortable !== false ? handleSort(column.key) : null"
                        >
                            <div
                                :class="[
                                    getAlignmentClass(column, index),
                                    index === 0 && !enableMultiSelect && enableDragDrop
                                        ? '!ppv2-px-4 ppv2-mx-auto '
                                        : 'ppv2-mx-auto px-2',
                                ]"
                                :style="{ minWidth: column.minWidth || 'max-content' }"
                            >
                                <slot :name="`header-${column.key}`" :column="column">
                                    <span
                                        class="ppv2-text-black poppinsSemiBold ppv2-tracking-wide"
                                        :class="column.headerClass || 'ppv2-text-lg md:ppv2-text-xl'"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                    >
                                        {{ column.label }}
                                    </span>
                                    <ChevronDown
                                        v-if="column.sortable !== false"
                                        :size="20"
                                        class="ppv2-w-5 ppv2-h-5"
                                        color="#191D41"
                                    />
                                </slot>
                            </div>
                        </th>
                    </tr>
                </thead>

                <!-- Table Body -->
                <tbody ref="tableBody">
                    <tr v-if="loading">
                        <td
                            :colspan="columns.length + (enableMultiSelect ? 1 : 0)"
                            :style="{ fontSize: normalizeFontSize(14) }"
                            class="ppv2-text-center ppv2-py-10"
                        >
                            <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2">
                                <div
                                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-5 ppv2-w-5 ppv2-border-2 ppv2-border-purple-600 ppv2-border-t-transparent"
                                ></div>
                                <span class="ppv2-text-black poppinsRegular">
                                    {{ $t('common.loading') || 'Chargement...' }}
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr v-else-if="items.length === 0">
                        <td
                            :colspan="columns.length + (enableMultiSelect ? 1 : 0)"
                            :style="{ fontSize: normalizeFontSize(14) }"
                            class="ppv2-text-center ppv2-py-10"
                        >
                            <span class="ppv2-text-black poppinsRegular">
                                {{ $t('datatable.noData') || 'Aucune donnée disponible' }}
                            </span>
                        </td>
                    </tr>
                    <tr
                        v-for="(item, index) in items"
                        :key="use_index ? index : item._uid || item.id || index"
                        class="hoverTR ppv2-relative ppv2-border-b ppv2-border-black ppv2-border-opacity-5 ppv2-transition-colors"
                        :class="[
                            { 'ppv2-bg-black ppv2-bg-opacity-5': index % 2 === 1 },
                            typeof rowClass === 'function' ? rowClass(item) : rowClass,
                        ]"
                        @click="$emit('row-click', item)"
                    >
                        <!-- Checkbox/Drag Column -->
                        <td v-if="enableMultiSelect" class="mobile-checkbox-col">
                            <div
                                v-if="enableDragDrop"
                                class="gripHandle ppv2-bg-black ppv2-bg-opacity-10 ppv2-rounded ppv2-p-[2px] ppv2-cursor-grab ppv2-absolute ppv2-left-[0px] ppv2-top-[50%] ppv2-translate-y-[-50%]"
                            >
                                <GripVertical :size="12" color="#FFFFFF80" />
                            </div>
                            <div v-if="enableMultiSelect" class="md:ppv2-pl-[15px]">
                                <BaseCheckbox
                                    size="md"
                                    :value="isItemSelected(use_index ? index : item.id)"
                                    @input="handleItemSelection(use_index ? index : item.id)"
                                    :has-border-checked="true"
                                />
                            </div>
                        </td>

                        <td
                            v-for="(column, colIndex) in columns"
                            :key="column.key"
                            class="ppv2-align-middle"
                            :class="[
                                column.cellClass || '',
                                getAlignmentClass(column, colIndex),
                                colIndex === 0 && !enableMultiSelect && enableDragDrop ? '!ppv2-px-4' : '',
                            ]"
                            :data-label="column.label"
                        >
                            <div
                                v-if="colIndex === 0 && !enableMultiSelect && enableDragDrop"
                                class="gripHandle ppv2-bg-black ppv2-bg-opacity-10 ppv2-rounded ppv2-p-[2px] ppv2-cursor-grab ppv2-absolute ppv2-left-[0px] ppv2-top-[50%] ppv2-translate-y-[-50%]"
                            >
                                <GripVertical :size="16" color="#FFFFFF80" />
                            </div>
                            <div
                                class="ppv2-mx-auto px-2"
                                :class="getAlignmentClass(column, colIndex)"
                                :style="{
                                    minWidth: column.minWidth || 'max-content',
                                    fontSize: normalizeFontSize(14),
                                }"
                            >
                                <slot :name="`column-${column.key}`" :item="item" :index="index" :column="column">
                                    <span
                                        ref="cellContent"
                                        class="ppv2-text-black poppinsRegular ppv2-inline-block ppv2-whitespace-nowrap"
                                        :class="getAlignmentClass(column, colIndex)"
                                        :data-column="column.key"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                    >
                                        {{ getNestedValue(item, column.key) }}
                                    </span>
                                </slot>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Bulk Actions Bar -->
        <div v-if="enableMultiSelect && selectedItems.length > 0" class="ppv2-flex ppv2-items-center ppv2-gap-4 ppv2-my-4">
            <slot name="bulk-actions" :selectedItems="selectedItems" :selectedCount="selectedItems.length">
                <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ selectedItems.length }} élément{{ selectedItems.length > 1 ? 's' : '' }} sélectionné{{
                        selectedItems.length > 1 ? 's' : ''
                    }}
                </span>
            </slot>
        </div>

        <!-- Pagination -->
        <div class="ppv2-mt-4">
            <Pagination
                v-if="totalPages >= 1"
                :columns="[]"
                :currentPage="currentPage"
                :totalPages="totalPages"
                :perPage="perPage"
                @page-changed="handlePageChange"
                @per-page-changed="$emit('per-page-changed', $event)"
            />
        </div>
    </div>
</template>

<script>
import { ChevronDown, GripVertical } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import BaseCheckbox from './BaseCheckbox.vue'
import Pagination from '../Customer/Pagination.vue'
import Sortable from 'sortablejs'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'DataTable',
    components: {
        ChevronDown,
        GripVertical,
        BaseCheckbox,
        Pagination,
    },
    props: {
        columns: {
            type: Array,
            required: true,
            // Format: [{ key: 'name', label: 'Nom', sortable: true, headerClass: '', cellClass: '', headerAlign: '' }]
        },
        items: {
            type: Array,
            required: true,
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        totalPages: {
            type: Number,
            default: 1,
        },
        enableDragDrop: {
            type: Boolean,
            default: true,
        },
        enableMultiSelect: {
            type: Boolean,
            default: true,
        },
        selectedItems: {
            type: Array,
            default: () => [],
        },
        rowClass: {
            type: [String, Function],
            default: '',
        },
        perPage: {
            type: Number,
            default: 10,
        },
        use_index: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            sortable: null,
            isMeasuring: false,
        }
    },
    computed: {
        isAllSelected() {
            return this.items.length > 0 && this.selectedItems.length === this.items.length
        },
    },
    watch: {
        // Recalculer les largeurs quand les données changent
        enableDragDrop: {
            handler(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.initSortable()
                    })
                } else if (this.sortable) {
                    this.sortable.destroy()
                    this.sortable = null
                }
            },
            immediate: true,
        },
    },
    mounted() {
        if (this.enableDragDrop) {
            this.initSortable()
        }
        // Écouter les changements de taille de fenêtre
        window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
        if (this.sortable) {
            this.sortable.destroy()
        }
        window.removeEventListener('resize', this.handleResize)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getNestedValue(obj, path) {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj)
        },
        isItemSelected(itemId) {
            return this.selectedItems.includes(itemId)
        },
        handleItemSelection(itemId) {
            this.$emit('item-selected', itemId)
        },
        handleSelectAllToggle() {
            this.$emit('select-all-toggled')
        },
        handlePageChange(page) {
            this.$emit('page-changed', page)
        },
        initSortable() {
            if (this.sortable) {
                this.sortable.destroy()
                this.sortable = null
            }

            const tbody = this.$refs.tableBody
            if (!tbody) return

            this.sortable = Sortable.create(tbody, {
                animation: 150,
                handle: '.gripHandle',
                ghostClass: 'ppv2-bg-purple-100',
                dragClass: 'ppv2-opacity-50',
                onEnd: this.handleDragEnd,
            })
        },
        handleDragEnd(evt) {
            this.$emit('drag-end', {
                oldIndex: evt.oldIndex,
                newIndex: evt.newIndex,
            })
        },
        handleSort(key) {
            this.$emit('sort', key)
        },
        getAlignmentClass(column, index) {
            // Rule 1: First column (excluding checkboxes) is ALWAYS left-aligned
            if (index === 0) {
                return 'ppv2-text-left'
            }

            // Rule 2: Use column type if defined
            if (column.type) {
                if (column.type === 'text') return 'ppv2-text-left'
                if (['number', 'date', 'boolean', 'badge', 'actions', 'image'].includes(column.type)) return 'ppv2-text-center'
            }

            // Rule 3: Use explicit align property
            if (column.align) {
                return column.align === 'center' ? 'ppv2-text-center' : 'ppv2-text-left'
            }

            // Default: Center for non-first columns
            return 'ppv2-text-center'
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }

    .gripHandle {
        color: #ffffff80;
    }
}

.headerColor {
    background-color: #7d2dbd26;
}

.gripHandle {
    color: #1a1f371a;
    opacity: 0;
    transition: opacity 0.3s ease;
}

tr:hover .gripHandle {
    opacity: 1;
}

.hoverTR:hover {
    background-color: #e5e5e5;
}

.dark .hoverTR:hover {
    background-color: rgb(255 255 255 / 5%);
}

/* Styles pour l'alignement */
table {
    table-layout: auto;
    /* Utiliser auto pour s'adapter au contenu */
}

td,
th {
    white-space: nowrap;
    /* Empêcher le texte de passer à la ligne */
}

/* S'assurer que toutes les cellules d'une colonne ont la même largeur */
td {
    min-width: max-content;
    /* Largeur minimale par défaut */
}

/* Pour le contenu des cellules */
.ppv2-inline-block {
    display: inline-block;
    text-align: left;
    /* Texte aligné à gauche */
}

.ppv2-whitespace-nowrap {
    white-space: nowrap;
}
td.mobile-checkbox-col {
    max-width: 35px !important;
    width: 35px !important;
}
/* --- Mobile Responsive: Card View Approach --- */
@media (max-width: 768px) {
    table,
    thead,
    tbody,
    th,
    td,
    tr {
        display: block;
    }
    thead {
        display: none;
    }
    tr.hoverTR {
        margin-bottom: 1rem;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        padding: 0.5rem;
        position: relative;
    }
    td {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        border: none !important;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
        padding: 5px !important;
        text-align: right !important;
        min-width: 100% !important;
        white-space: normal !important;
    }
    td:last-child {
        border-bottom: 0 !important;
    }
    /* Pseudo-element for column label */
    td[data-label]::before {
        content: attr(data-label);
        font-weight: 600;
        text-align: left;
        color: #718096;
        padding-right: 15px;
        white-space: nowrap;
        flex-shrink: 0;
        font-size: 12px;
    }
    /* Allow value side to wrap */
    td > div {
        margin-left: auto;
        margin-right: 0;
        white-space: normal;
        text-align: right;
        /*width: 100%;*/
    }
    td > div .ppv2-flex,
    td > div .ppv2-mx-auto.px-2.ppv2-text-left {
        justify-content: end;
        text-align: right;
    }
    /* Checkbox cell styling on mobile */
    td.mobile-checkbox-col {
        display: flex !important;
        justify-content: start !important;
        background-color: rgba(0, 0, 0, 0.02);
        border-radius: 4px;
        margin-bottom: 10px;
        padding: 10px !important;
    }
    td.mobile-checkbox-col > div {
        margin: 0;
    }
    .ppv2-whitespace-nowrap {
        white-space: normal !important;
    }
}
</style>
